import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";

import { AppShell } from "@/components/shell/AppShell";
import { RequireAuth } from "@/components/shell/RequireAuth";
import { AlertCentre } from "@/routes/alerts/AlertCentre";
import { AlertInstanceHistory } from "@/routes/alerts/AlertInstanceHistory";
import { AlertsList } from "@/routes/alerts/AlertsList";
import { AcceptInvitation } from "@/routes/auth/AcceptInvitation";
import { ForgotPassword } from "@/routes/auth/ForgotPassword";
import { ResetPassword } from "@/routes/auth/ResetPassword";
import { SignIn } from "@/routes/auth/SignIn";
import { BillsCompliance } from "@/routes/BillsCompliance";
import { DocumentsList } from "@/routes/documents/DocumentsList";
import { Equipment } from "@/routes/Equipment";
import { EquipmentRecord } from "@/routes/equipment/EquipmentRecord";
import { Home } from "@/routes/Home";
import { LeaseRecord } from "@/routes/leases/LeaseRecord";
import { LeasesList } from "@/routes/leases/LeasesList";
import { Money } from "@/routes/Money";
import { MunicipalitiesList } from "@/routes/municipalities/MunicipalitiesList";
import { MunicipalityRecord } from "@/routes/municipalities/MunicipalityRecord";
import { RenewalBoard } from "@/routes/renewals/RenewalBoard";
import { RenewalRecord } from "@/routes/renewals/RenewalRecord";
import { PropertiesList } from "@/routes/properties/PropertiesList";
import { PropertyRecord } from "@/routes/properties/PropertyRecord";
import { Settings } from "@/routes/Settings";
import { BackupStatus } from "@/routes/settings/BackupStatus";
import { ImportBatches } from "@/routes/settings/ImportBatches";
import { ImportWizard } from "@/routes/settings/ImportWizard";
import { ReferenceLists } from "@/routes/settings/ReferenceLists";
import { Status } from "@/routes/Status";
import { TenantRecord } from "@/routes/tenants/TenantRecord";
import { TenantsList } from "@/routes/tenants/TenantsList";
import { UserRecord } from "@/routes/users/UserRecord";
import { UsersList } from "@/routes/users/UsersList";
import { VendorRecord } from "@/routes/vendors/VendorRecord";
import { VendorsList } from "@/routes/vendors/VendorsList";
import { WorkOrders } from "@/routes/WorkOrders";

import "@/index.css";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

// Gap-fill (frontend auth UI), judgement call 1 — the four auth screens are
// top-level siblings of the `RequireAuth`/`AppShell` layout tree, not
// children of it: a signed-out visitor loading `/sign-in` (or any of the
// other three) must never pass through the auth gate at all, and must never
// see the nav rail/bottom bar `AppShell` renders. Each is registered with
// its own real route, added to `route-table.test.ts`'s own
// `EXTRA_ALLOWED_PATHS`.
//
// `RequireAuth` wraps `AppShell`'s own layout route as one more layout route
// above it — every one of the nine destinations, `/status` and every
// extra path below renders behind the gate without any of them changing
// themselves (`RequireAuth.tsx`'s own doc comment for the gate's exact
// behaviour).
const router = createBrowserRouter([
  { path: "/sign-in", element: <SignIn /> },
  { path: "/accept-invitation", element: <AcceptInvitation /> },
  { path: "/forgot-password", element: <ForgotPassword /> },
  { path: "/reset-password", element: <ResetPassword /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: "/", element: <Home /> },
          // Story 5.3 replaces the placeholder with the real list and record
          // page — `/properties/:id` is an extra path beyond the nine
          // destinations (`route-table.test.ts`'s own `EXTRA_ALLOWED_PATHS`),
          // the same shape `/settings/users/:id` already established for Users.
          { path: "/properties", element: <PropertiesList /> },
          { path: "/properties/:id", element: <PropertyRecord /> },
          // Story 6.1 replaces the placeholder with the real tenants list and
          // record page — `/tenants-leases/:id` is an extra path beyond the
          // nine destinations (`route-table.test.ts`'s own `EXTRA_ALLOWED_
          // PATHS`), the same shape `/properties/:id` already established for
          // Properties. This nav slot's own list is the tenants half only —
          // still true after Story 6.5 (see `TenantsList.tsx`'s own docstring
          // and `LeaseRecord.tsx`'s own routing doc comment for why no lease
          // list screen is added here).
          { path: "/tenants-leases", element: <TenantsList /> },
          { path: "/tenants-leases/:id", element: <TenantRecord /> },
          // Story 6.5's own real lease record page — a sibling path under the
          // same nav destination, deliberately not the bare `/tenants-leases/
          // :id` above (which stays the tenant record's own path). See
          // `LeaseRecord.tsx`'s own doc comment for the full routing reasoning.
          { path: "/tenants-leases/leases/:id", element: <LeaseRecord /> },
          // Story 6.7's own first-ever leases list — a second sibling path
          // under the same `/tenants-leases` nav destination, alongside the
          // lease record page above. See `LeasesList.tsx`'s own doc comment for
          // the full routing reasoning.
          { path: "/tenants-leases/leases", element: <LeasesList /> },
          { path: "/equipment", element: <Equipment /> },
          // Story 12.1: the real equipment record page — a sibling path
          // under the same `/equipment` nav destination, alongside the
          // portfolio-wide list above.
          { path: "/equipment/:id", element: <EquipmentRecord /> },
          { path: "/bills-compliance", element: <BillsCompliance /> },
          { path: "/money", element: <Money /> },
          { path: "/work-orders", element: <WorkOrders /> },
          // Story 7.2 replaces the placeholder with the real, shared-data-table
          // documents list (`DocumentsList.tsx`'s own doc comment).
          { path: "/documents", element: <DocumentsList /> },
          // Story 16.2 — the real "Vendors" nav destination (the genuine
          // tenth, `lib/destinations.ts`'s own doc comment) and its own
          // record page, `/vendors/:id`, a sibling path under it mirroring
          // `/properties/:id`'s/`/tenants-leases/:id`'s identical
          // "record page, sub-path of its own list destination" shape.
          { path: "/vendors", element: <VendorsList /> },
          { path: "/vendors/:id", element: <VendorRecord /> },
          { path: "/settings", element: <Settings /> },
          // Story 2.2's first real child under Settings (spec judgement call
          // 5) — the shared table's live proof against Story 1.7's `GET
          // /users`. Story 2.5 replaces the record placeholder with the real
          // record-page shell, fetching its own record through `GET
          // /users/{id}` directly.
          { path: "/settings/users", element: <UsersList /> },
          { path: "/settings/users/:id", element: <UserRecord /> },
          // Story 3.8's own child under Settings — the shared table's second
          // real proof, against `GET /alert-rules` this time. No record page:
          // there is nothing beyond what one row already shows.
          { path: "/settings/alerts", element: <AlertsList /> },
          // Story 4.5's own child under Settings — the third real screen, R-B8/
          // R-B6's visible half. No record page: there is nothing beyond what
          // the top figures and the history table already show.
          { path: "/settings/backup", element: <BackupStatus /> },
          // Story 5.2's own children: the fourth child of Settings — the one
          // generic, list_key-parameterized reference-list admin screen (spec
          // judgement call 8) — and the municipality record page, reached
          // directly by id (no list screen of its own yet: nothing in this
          // story's own scope links a property to a municipality, so there is
          // no real navigation path to a specific town beyond a direct URL
          // until Story 5.3's own Property record links here).
          { path: "/settings/reference-lists", element: <ReferenceLists /> },
          // Story 8.2's own fifth child of Settings — the CSV lease import
          // wizard, at its own full-page route rather than a Dialog (see
          // `ImportWizard.tsx`'s own doc comment for the full routing
          // reasoning).
          { path: "/settings/import", element: <ImportWizard /> },
          { path: "/settings/import-batches", element: <ImportBatches /> },
          // Story 15.1's own child of Settings — the municipality list
          // screen, following the exact `/settings/reference-lists`/
          // `/settings/import-batches` "one more Settings sub-route, no
          // tenth nav destination" shape. `/municipalities/:id` (below)
          // stays outside `/settings` — it already shipped that way in
          // Story 5.2 and this story leaves it untouched.
          { path: "/settings/municipalities", element: <MunicipalitiesList /> },
          { path: "/municipalities/:id", element: <MunicipalityRecord /> },
          // Story 11.1: no "Renewals" nav destination/list screen yet
          // (spec judgement call 5) — reachable only via this direct URL
          // or a link from the source Lease's own record page.
          { path: "/renewals/:id", element: <RenewalRecord /> },
          // Story 11.3: the renewal board — a third sibling path under the
          // existing `/tenants-leases` destination, alongside the tenants
          // list (`/tenants-leases`) and the leases list (`/tenants-leases/
          // leases`) above. `EXPERIENCE.md`'s own routing table names this
          // exact nav entry point ("Tenants & Leases -> 'Renewal board'"),
          // and spec judgement call 3 resolves it the minimal way: a real
          // link from the Leases list and the Tenants list (this
          // destination's own landing page) rather than a new nav-rail
          // structure — see both screens' own doc comments.
          { path: "/tenants-leases/renewals", element: <RenewalBoard /> },
          // The health check, moved from `/` (spec judgement call 2) — kept
          // exactly as Story 1.4 built it, just relocated.
          { path: "/status", element: <Status /> },
          // Story 3.9's alert centre — reached from the bell on every screen
          // (EXPERIENCE.md's IA table), deliberately not a tenth nav-rail
          // destination of its own.
          { path: "/alerts", element: <AlertCentre /> },
          { path: "/alerts/:id", element: <AlertInstanceHistory /> },
        ],
      },
    ],
  },
]);

const container = document.getElementById("root");
if (!container) {
  throw new Error("index.html is missing its #root element");
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
